import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sendPasswordReset } from '../../firebase/auth.service';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const trimmedEmail = email.trim().toLowerCase();

        if (!trimmedEmail) {
            setError('Please enter your email address.');
            return;
        }

        // Basic email format check
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setError('Please enter a valid email address.');
            return;
        }

        setLoading(true);
        try {
            await sendPasswordReset(trimmedEmail);
            setSuccess('Password reset link sent! Check your email inbox.');
            // Redirect to login after a short delay
            setTimeout(() => {
                navigate('/login', {
                    state: { resetSuccess: 'Password reset email sent. Check your inbox and follow the link to reset your password.' },
                });
            }, 2000);
        } catch (err) {
            console.error('Password reset error:', err);
            const code = err.code || '';
            if (code === 'auth/user-not-found') {
                setError('No account found with this email address.');
            } else if (code === 'auth/invalid-email') {
                setError('Please enter a valid email address.');
            } else if (code === 'auth/too-many-requests') {
                setError('Too many requests. Please try again later.');
            } else {
                setError(err.message || 'Failed to send reset email. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-2 py-8">
            <form className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 md:p-10 flex flex-col gap-2" onSubmit={handleSubmit}>
                <h2 className="text-2xl md:text-3xl font-bold mb-1">Reset Password</h2>
                <p className="text-gray-500 text-base mb-4">
                    Enter the email address associated with your account and we'll send you a link to reset your password.
                </p>

                <label className="text-sm font-medium">Email</label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">📧</span>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        className="w-full bg-transparent outline-none py-3 text-base"
                        disabled={loading}
                        autoFocus
                    />
                </div>

                {error && <div className="text-red-500 text-sm text-center mt-2">{error}</div>}
                {success && <div className="text-green-600 text-sm text-center mt-2">{success}</div>}

                <button
                    className="w-full bg-black text-white rounded-md py-3 font-semibold text-lg mt-4 hover:bg-gray-900 transition-colors disabled:opacity-60"
                    type="submit"
                    disabled={loading}
                >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                </button>

                <div className="text-center text-sm text-gray-600 mt-4">
                    <Link to="/login" className="text-black font-semibold hover:underline">Back to Login</Link>
                </div>
            </form>
        </div>
    );
};

export default ForgotPassword;
